import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Site } from 'src/app/models/site.model';
import { Notification, User } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth/auth.service';
import { UserService } from 'src/app/services/user/user.service';
import { UtilsService } from 'src/app/services/util/util-service';

import { LocalizationService } from '@app/components/internationalization/localization.service';
import { NotificationRetry } from '@app/models/notification.model';
import { Capacitor } from '@capacitor/core';
import { Position } from '@capacitor/geolocation';
import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  RegistrationError,
  Token,
} from '@capacitor/push-notifications';
import { ConfigurationService } from '../configuration/configuration.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationPushService {
    private deviceToken: string = '';
    private notificationId: string | undefined;
    currentUser: User | undefined;
    private outOfRangeCheck: boolean = false;
    private currentSite: Site | undefined;

  constructor(
    public _router: Router,
    private _authService: AuthService,
    private _userService: UserService,
    private _utilsService: UtilsService,
    private _localizationService : LocalizationService,
    private _ngZone: NgZone,
    private _configurationService : ConfigurationService
  ){
    this._authService.getUser()
    .then(user => this.currentUser = user);

    if (Capacitor.isPluginAvailable('PushNotifications')){

      PushNotifications.removeAllListeners();

      PushNotifications.addListener('registration',
        (token: Token) => {
          this.deviceToken = token.value;
        }
      );

      PushNotifications.addListener('registrationError', 
        (error: RegistrationError) => {
          console.log('Error on registration: ' + JSON.stringify(error));
        }
      );

      PushNotifications.addListener('pushNotificationReceived',
        (notification: PushNotificationSchema) => {
          this._ngZone.run(() => {
            alert(this._localizationService.translate("notification_outofrange"));
            this._router.navigate(['siteinductioncompleted']);
          });                
        }
      );

      PushNotifications.addListener('pushNotificationActionPerformed',
        (notification: ActionPerformed) => {
          _ngZone.run(() => {
            console.log('Push action performed: ' + JSON.stringify(notification));
            this._router.navigate(['siteinductioncompleted']);
          });
        }
      );
    }        
  }

  registerNotifications = async () => {
    if(this._utilsService.isMobileAndTablet()){  
      let permStatus = await PushNotifications.checkPermissions();
    
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }
    
      if (permStatus.receive === 'granted') {
        await PushNotifications.register();
      }

    }
  }

  async startOutOfRangeCheck(site: Site): Promise<void> {
    if (site && site.isGeofencingEnable) {
      let deviceType = this.getDeviceType();
      this.currentSite = site;

      if (this._utilsService.isMobileAndTablet() && deviceType != 'web') {
        if(this.notificationId == undefined && this.deviceToken && this.currentUser?.id){
          this._userService.registerForNotification({
            id: undefined,
            userId: this.currentUser?.id,
            deviceType: deviceType,
            deviceToken: this.deviceToken,
            content: 'Please sign out of the site.'
          } as Notification)
            .subscribe({
              next: notification => {
                if (notification) {
                  this.notificationId = notification.id
                  this.outOfRangeCheck = true;
                }
              },
              error: error => {
                console.log("registerForNotification :" + JSON.stringify(error));
              },
            });
        }
      }
      else {
        this.outOfRangeCheck = true;
      } 
      
    }
    
  }

  async stopOutOfRangeCheck(): Promise<void> {
    this.outOfRangeCheck = false;
    this.currentSite = undefined;
  }

  async locationRangeCheck(latestLocation: Position): Promise<void> {
    if (this.currentSite && latestLocation) {
       if ( !this._utilsService.isUndefinedOrNull(latestLocation?.coords?.latitude) &&
          !this._utilsService.isUndefinedOrNull(latestLocation?.coords?.longitude) &&
          !this._utilsService.isUndefinedOrNull(this.currentSite.lat) &&
          !this._utilsService.isUndefinedOrNull(this.currentSite.lon)) {
          const distanceInKilometers = this._utilsService.getDistanceBetweenTwoPointsinKM(latestLocation?.coords?.latitude,
            latestLocation?.coords?.longitude, this.currentSite.lat, this.currentSite.lon);
          const distanceInMeters = this._utilsService.convertKilometersToMeters(distanceInKilometers);

          if (!this._utilsService.checkSiteIsInRange(this.currentSite, distanceInMeters)) {
            await this.outOfRangeAction();
          } else {
            await this._userService.setOutOfRange("false");
          }
        }
    }
  }

  private async outOfRangeAction() {
    await this._userService.setOutOfRange("true");
    let deviceType = this.getDeviceType();

    if (this._utilsService.isMobileAndTablet() && deviceType != 'web') {
      if (this.notificationId !== undefined) {
          let shouldRetryNotification = await this.shouldRetryNotificationCheck();
          if (shouldRetryNotification) {
            this._userService.pushNotification(this.notificationId as string)
            .subscribe()
            .add(async () => {});
          }
      }

    } else {
      alert(this._localizationService.translate("notification_outofrange"));
      this.outOfRangeCheck = false;
    }
  }

  private async shouldRetryNotificationCheck(): Promise<boolean>{
    let shouldRetryNotification = false;
    let retryObj = await this._userService.getOutOfRangeRetryCount();
    let interval = await this._configurationService.getOutOfRangeNotificationIntervalInMinutes();
    let retryMaxCount = await this._configurationService.getOutOfRangeNotificationMaxRetryCount();

    if(retryObj && retryObj != null && retryObj?.retryCount != undefined && retryObj?.dateTimeToNextRetry != undefined){
      
      if(retryObj.retryCount < retryObj.retryMaxCount && new Date().getTime() >= retryObj.dateTimeToNextRetry){ 
        shouldRetryNotification = true;
        retryObj.dateTimeToNextRetry = new Date().getTime() + (interval * 60 * 1000);
        retryObj.retryCount += 1;
        await this._userService.setOutOfRangeRetryCount(JSON.stringify(retryObj));
      }

    }else{
      shouldRetryNotification = true;
      retryObj = new NotificationRetry();
      retryObj.retryCount = 0;
      retryObj.dateTimeToNextRetry = new Date().getTime() + (interval * 60 * 1000);
      retryObj.retryMaxCount = retryMaxCount;
      await this._userService.setOutOfRangeRetryCount(JSON.stringify(retryObj));
    }

    return shouldRetryNotification;
  }


  private getDeviceType(): string {
    let deviceType = "web";
    switch(Capacitor.getPlatform()) {
      case "ios":
        deviceType = "apns";
        break;
      case "android":
        deviceType = "fcm";
          break;
      default:
        deviceType = "web";
    };
    return deviceType;
  }

  isOutOfRangeCheck(): boolean {
    return this.outOfRangeCheck;
  }
}